import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  TableCell,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  Dialog,
  TextField,
  Tooltip,
} from '@mui/material';
import { DateCalendar, MonthCalendar } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { Card404, DataNotFound, Iconify, Scrollbar, StatusTag } from '@app/components';
import { Strings } from '@app/constants';
import { useDialog, useRequestState } from '@app/hooks';
import { Header } from '@app/sections/header';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { StyledTitle } from '../style';
import { selectGetFilteredInternsState } from '../selecter';
import { getFilteredInterns } from '../slice';
import { IInterns } from '../types';
import { PATH_DASHBOARD } from '@app/routes/paths';

const SelectedInterns = () => {
  const monthPickerDialog = useDialog();
  const datePickerDialog = useDialog();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClear = () => {
    setSelectedDate(null);
    setSelectedMonth(null);
    setSelectedFilter('');
  };

  const open = Boolean(anchorEl);
  const [selectedFilter, setSelectedFilter] = useState('');

  const handleFilterOptionClick = (option: string) => {
    setSelectedFilter(option);
    if (option === 'byDate') {
      datePickerDialog.show();
    } else {
      monthPickerDialog.show();
    }
    handleClose();
  };
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs | null>(null);
  const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
  const formattedMonth = dayjs(selectedMonth).format('MMMM');

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    setSelectedDate(newValue);
    datePickerDialog.hide();
  };

  const handleMonthChange = (newValue: dayjs.Dayjs | null) => {
    setSelectedMonth(newValue);
    monthPickerDialog.hide();
  };

  const dispatch = useDispatch();

  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  useEffect(() => {
    let params = {};
    if (selectedFilter === 'byDate' && formattedDate !== 'Invalid Date') {
      params = { formattedDate };
    } else if (selectedFilter === 'byMonth' && formattedMonth !== 'Invalid Date') {
      params = { formattedMonth };
    } else {
      params = { currentMonth };
    }

    if (Object.keys(params).length > 0) {
      dispatch(getFilteredInterns(params));
    }
  }, [dispatch, formattedDate, formattedMonth, selectedFilter, currentMonth]);
  const { data } = useRequestState({
    stateSelector: selectGetFilteredInternsState,
  });
  const navigate = useNavigate();

  const getStatus = (intern: IInterns) => {
    return data?.employees?.some((employee: IInterns) => employee.userId === intern.userId)
      ? 'PromotedAsEmployee'
      : data?.internFinalWeek?.some(
          (internFinalWeek: IInterns) => internFinalWeek.userId === intern.userId
        )
      ? 'InternFinalWeek'
      : 'Intern';
  };

  return (
    <Card sx={{ mb: 2, borderRadius: '5px', ml: { xs: 1, md: 2 }, mr: { xs: 1, md: 2 } }}>
      <Stack flexDirection={{ sm: 'column', md: 'row' }}>
        <Card
          sx={{
            m: { xs: 0, md: 2 },
            borderRadius: '5px',
            width: { md: '43%', xs: '100%' },
            boxShadow: { xs: 0, md: 1 },
          }}
        >
          <Stack direction={'row'} justifyContent={'space-between'} p={1.5}>
            <Stack fontSize={17} fontWeight={700} justifyContent={'center'}>
              {Strings.page_title.interns}
            </Stack>
            <Stack width={'32%'} mb={1} p={-1}>
              {!selectedDate && !selectedMonth && (
                <TextField name="input" value={currentMonth} disabled size="small" />
              )}
              {selectedFilter === 'byDate' && selectedDate && (
                <TextField name="input" value={formattedDate} disabled size="small" />
              )}
              {selectedFilter === 'byMonth' && selectedMonth && (
                <TextField name="input" value={formattedMonth} disabled size="small" />
              )}
            </Stack>
            {selectedDate ?? selectedMonth ? (
              <Tooltip title="Clear Filter">
                <Iconify
                  icon="tdesign:filter-clear"
                  onClick={handleClear}
                  sx={{ cursor: 'pointer', mr: 3, mt: 1 }}
                />
              </Tooltip>
            ) : (
              <Iconify
                icon="octicon:filter-16"
                onClick={handleClick}
                sx={{ cursor: 'pointer', mr: 3, mt: 1 }}
              />
            )}

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                sx={{ fontSize: 13 }}
                onClick={() => {
                  handleFilterOptionClick('byDate');
                }}
              >
                {Strings.dashboard.byDate}
              </MenuItem>
              <MenuItem
                sx={{ fontSize: 13 }}
                onClick={() => {
                  handleFilterOptionClick('byMonth');
                }}
              >
                {Strings.dashboard.byMonth}
              </MenuItem>
            </Menu>
          </Stack>
          <Scrollbar
            sx={{
              height: 370,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              '-ms-overflow-style': 'none',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{Strings.field.name}</TableCell>
                  <TableCell align="center">{Strings.dashboard.selectionDate}</TableCell>
                  <TableCell align="center">{Strings.field.status}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.interns?.length > 0 ? (
                  data?.interns?.map((intern: IInterns, index: number) => {
                    const openDetailsPage = () => {
                      navigate(`${PATH_DASHBOARD.employeeDetail}/${intern.user.firstName}`, {
                        state: { id: intern.userId },
                      });
                    };

                    return (
                      <TableRow key={index}>
                        <TableCell
                          align="center"
                          onClick={openDetailsPage}
                        >{`${intern.user.firstName} ${intern.user.lastName}`}</TableCell>
                        <TableCell align="center" onClick={openDetailsPage}>
                          {dayjs(intern.actionDate).format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell align="center" onClick={openDetailsPage}>
                          <StatusTag status={getStatus(intern)} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow sx={{ '& td, & th': { borderBottom: 'none' } }}>
                    <TableCell colSpan={4}>
                      <Card404 height={240} message="Data Not Found!" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {datePickerDialog.visible && (
              <Dialog
                open
                PaperProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '300px',
                  },
                }}
                onClose={() => {
                  datePickerDialog.hide();
                }}
              >
                <Header variant="dialog" title={'Select date'} onClose={datePickerDialog.hide} />
                <DateCalendar onChange={handleDateChange} />
              </Dialog>
            )}
            {monthPickerDialog.visible && (
              <Dialog
                open
                PaperProps={{
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '300px',
                  },
                }}
                onClose={() => {
                  monthPickerDialog.hide();
                }}
              >
                <Header variant="dialog" title={'Select Month'} onClose={monthPickerDialog.hide} />
                <MonthCalendar value={selectedMonth} onChange={handleMonthChange} />
              </Dialog>
            )}
          </Scrollbar>
        </Card>
        <Stack sx={{ width: { md: '57%', sm: '100%' }, mt: 1 }}>
          <Stack sx={{ height: { md: 200 }, maxHeight: { xs: 200 } }} spacing={-0.4}>
            <StyledTitle>{Strings.dashboard.internShipFinalWeek}</StyledTitle>
            <Scrollbar>
              {data?.internFinalWeek?.length > 0 ? (
                data.internFinalWeek.map((option: IInterns) => {
                  const openDetailsPage = () => {
                    navigate(`${PATH_DASHBOARD.internDetail}/${option.user.firstName}`, {
                      state: { id: option.userId },
                    });
                  };
                  return (
                    <InfoStatusCard
                      key={option.id}
                      titleName={`${String(option.user.firstName) ?? ''} ${
                        String(option.user.lastName) ?? ''
                      }`}
                      subTitleHead={`${Strings.dashboard.selectionDate}:`}
                      subTitleValue={dayjs(option.selectionDate).format('DD/MM/YYYY')}
                      actionHead={
                        option?.user?.review?.reviewedBy && `${Strings.dashboard.reviewedBy}:`
                      }
                      status={option.user.review?.overall}
                      actionSubValue={
                        option?.user?.review?.reviewedBy && option?.user?.review?.reviewedBy
                      }
                      onClick={openDetailsPage}
                    />
                  );
                })
              ) : (
                <DataNotFound height={170} />
              )}
            </Scrollbar>
          </Stack>

          <Stack sx={{ height: { md: 250 }, maxHeight: { xs: 250 } }} spacing={-0.4} mt={1}>
            <StyledTitle>{Strings.rowActions.promoteAsEmployee}</StyledTitle>
            <Scrollbar>
              {data?.employees?.length > 0 ? (
                data.employees.map((option: IInterns) => {
                  const openDetailsPage = () => {
                    navigate(`${PATH_DASHBOARD.employeeDetail}/${option.user.firstName}`, {
                      state: { id: option.userId },
                    });
                  };
                  return (
                    <InfoStatusCard
                      key={option.id}
                      titleName={`${String(option.user.firstName) ?? ''} ${
                        String(option.user.lastName) ?? ''
                      }`}
                      subTitleHead={`${Strings.dashboard.selectionDate}:`}
                      subTitleValue={dayjs(option.promotionDate).format('DD/MM/YYYY')}
                      actionHead={
                        option?.actionedByUser &&
                        `Promoted By: ${option?.actionedByUser?.firstName} ${option?.actionedByUser?.lastName}`
                      }
                      onClick={openDetailsPage}
                    />
                  );
                })
              ) : (
                <DataNotFound height={200} />
              )}
            </Scrollbar>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

export { SelectedInterns };
